import { useState, useEffect, useCallback } from 'react';
import { api, ConversationMessage } from '../lib/api';

interface UseConversationResult {
  messages: ConversationMessage[];
  conversationId: string | null;
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  sendMessage: (message: string) => Promise<void>;
  refresh: () => void;
}

export function useConversation(emailId: string | null, accountId?: string): UseConversationResult {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConversation = useCallback(async () => {
    if (!emailId) {
      setMessages([]);
      setConversationId(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await api.getConversation(emailId, accountId);
      setMessages(result.messages || []);
      setConversationId(result.conversationId || null);
    } catch (err) {
      console.error('Failed to fetch conversation:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch conversation');
      setMessages([]);
      setConversationId(null);
    } finally {
      setIsLoading(false);
    }
  }, [emailId, accountId]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  const sendMessage = useCallback(async (message: string) => {
    if (!emailId) return;

    setIsSending(true);
    setError(null);

    // Optimistically add user message
    const userMessage: ConversationMessage = {
      id: `temp_${Date.now()}`,
      conversationId: conversationId || 'new',
      role: 'user',
      content: message,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const result = await api.sendMessage(
        conversationId || 'new',
        message,
        emailId
      );

      // Update conversation ID if new
      if (result.conversationId && result.conversationId !== conversationId) {
        setConversationId(result.conversationId);
      }

      // Add assistant response
      const assistantMessage: ConversationMessage = {
        id: `msg_${Date.now()}`,
        conversationId: result.conversationId,
        role: 'assistant',
        content: result.response,
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setIsSending(false);
    }
  }, [emailId, conversationId]);

  return {
    messages,
    conversationId,
    isLoading,
    isSending,
    error,
    sendMessage,
    refresh: fetchConversation,
  };
}
